import React, { useContext, useState } from 'react';
import parse from 'html-react-parser';
import { Alert, Box, Button, Card, CardContent, CardHeader, IconButton, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import useAssignmentMetrics from '../_data_sources/useAssignmentMetrics';
import usePendingAssignmentsSummary from '../_data_sources/usePendingAssignmentSummary';
import useOverdueWarnings from '../_data_sources/useOverdueWarnings';
import { error, neutral, success, warning } from '_lex/_foundations/phxColors';
import GroupPicker from '../../_components/GroupPicker';
import Loading from 'views/App/Portal/Loading';
import PendingAssignmentsSummaryDisplay from './PendingAssignmentsSummary';
import { ChevronRight, MailOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getLmsUrl } from 'data_sources/constants';

type AssignmentMetricStyles = {
  overdue: any;
  dueSoon: any;
  completed: any;
};

export default function Assignments() {
  const navigate = useNavigate();
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const [group, setGroup] = useState<string | null>(null);
  const metricsQuery = useAssignmentMetrics(currentOrganizationId, { group });
  const pendingAssignmentsQuery = usePendingAssignmentsSummary(currentOrganizationId, { group });
  const overdueWarningsQuery = useOverdueWarnings(currentOrganizationId, { group });
  const assignmentMetricStyles: AssignmentMetricStyles = {
    overdue: {
      backgroundColor: error[25],
      borderColor: error[600],
    },
    dueSoon: {
      backgroundColor: warning[25],
      borderColor: warning[600],
    },
    completed: {
      backgroundColor: success[25],
      borderColor: success[600],
    },
  };

  const allowGroupPicker = false;

  const handleNavigationToViewAllForUser = (id: string) => {
    navigate(`/admin/reporting/policy-acknowledgments#users=%5B"${id}"%5D&status=OVERDUE`);
  };
  return (
    <Card className='assignment-metrics'>
      <CardHeader
        title={t('dashboard.training.assignments.title')}
        action={
          <>
            {hasPermission('listGroups', currentOrganizationId) && allowGroupPicker && (
              <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupPicker
                  id='groups_filter'
                  size='small'
                  placeholder={t('dashboard.training.assignments.groupFilter.placeholder')}
                  organizationId={currentOrganizationId}
                  value={group ? [group] : undefined}
                  onChange={(value) => setGroup(value[0])}
                  allowBlank
                />
              </Box>
            )}
          </>
        }
      />
      <CardContent>
        <Stack spacing={8}>
          <Stack spacing={4}>
            <Box sx={{ display: 'flex', gap: 12, alignItems: 'end', justifyContent: 'space-between' }}>
              <Box
                sx={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 3,
                }}
              >
                {Object.entries(metricsQuery.data ?? {}).map(([key, value], index) => (
                  <Box key={`metric_box_${index}`}>
                    <Box
                      key={`metric_${key}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 3,
                        py: 2,
                        borderRadius: '4px 16px 4px 4px',
                        border: `solid 1px ${neutral[200]}`,
                        ...(assignmentMetricStyles[key as keyof AssignmentMetricStyles] ?? {}),
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 24,
                          fontWeight: 500,
                          color: neutral[800],
                        }}
                      >
                        {value}
                      </Typography>
                      <Typography
                        variant='body1'
                        sx={{
                          fontSize: 18,
                          color: neutral[600],
                          fontWeight: 400,
                        }}
                      >
                        {parse(t(`dashboard.training.assignments.metrics.${key}`))}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Button
                color='secondary'
                size='small'
                endIcon={<ChevronRight />}
                href={getLmsUrl('/assigned-learning/assignments')}
              >
                {t('dashboard.training.assignments.viewAll')}
              </Button>
            </Box>
            <Box>
              {pendingAssignmentsQuery.isLoading ? (
                <Loading />
              ) : (
                <>
                  {!pendingAssignmentsQuery.data || pendingAssignmentsQuery.data.length === 0 ? (
                    <Alert severity='info'>{t('dashboard.training.assignments.empty')}</Alert>
                  ) : (
                    <Stack
                      sx={{
                        border: `solid 1px ${neutral[200]}`,
                        borderRadius: 2,
                        '& .pending-assignments-summary': {
                          borderBottom: `1px solid ${neutral[300]}`,
                          '&:last-of-type': {
                            borderBottom: 0,
                          },
                        },
                      }}
                    >
                      {pendingAssignmentsQuery.data.map((summary: any, ind: number) => (
                        <PendingAssignmentsSummaryDisplay
                          key={`summary_${ind}`}
                          summary={summary}
                        />
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Box>
            <Box>
              {overdueWarningsQuery.isLoading ? (
                <Loading />
              ) : (
                <Card className='assignment-metrics'>
                  <CardHeader
                    title={t('dashboard.training.assignments.overdueWarnings.title')}
                    action={
                      <>
                        <Button
                          color='secondary'
                          size='small'
                          endIcon={<ChevronRight />}
                          href={getLmsUrl('/assigned-learning/assignment-progress')}
                        >
                          {t('dashboard.training.assignments.overdueWarnings.viewAll')}
                        </Button>
                      </>
                    }
                  />
                  <CardContent>
                    {!overdueWarningsQuery.data || overdueWarningsQuery.data.length === 0 ? (
                      <Alert severity='info'>{t('dashboard.training.assignments.overdueWarnings.empty')}</Alert>
                    ) : (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: 3,
                        }}
                      >
                        {overdueWarningsQuery.data.map((item: any) => (
                          <Box
                            key={`overdue_warning_${item.id}`}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr',
                              px: 4,
                              py: 3,
                              border: `solid 1px ${neutral[200]}`,
                              borderRadius: 2,
                              backgroundColor: neutral[50],
                            }}
                          >
                            <Stack>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  flex: 1,
                                }}
                              >
                                <Typography>{[item.firstName, item.lastName].join(' ').trim()}</Typography>
                                <IconButton href={`mailto:${item.email}`}>
                                  <MailOutlined />
                                </IconButton>
                              </Box>
                              <Typography sx={{ color: error[800] }}>
                                {item.overdueAssignments === 1
                                  ? t('dashboard.training.assignments.overdueWarnings.overdueAssignmentsSingular', '', {
                                      count: item.overdueAssignments.toString(),
                                    })
                                  : t('dashboard.training.assignments.overdueWarnings.overdueAssignments', '', {
                                      count: item.overdueAssignments.toString(),
                                    })}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
